<template>
    <canvas class="confetti-rain" ref="canvas" v-if="running" aria-hidden="true"></canvas>
</template>

<script>
const FIGMA_COLORS = ["#ffc700", "#61ba47", "#007aff", "#f74f9e", "#ff5257", "#a550a7"];

const MAX_STEP = 0.05;

export default {
    name: "ConfettiRain",
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        duration: {
            type: Number,
            default: 4000,
        },
        particleCount: {
            type: Number,
            default: 150,
        },
        colors: {
            type: Array,
            default: () => FIGMA_COLORS,
        },
    },
    data() {
        return {
            running: false,
        };
    },
    created() {
        this.particles = [];
        this.ctx = null;
        this.frame = null;
        this.startedAt = 0;
        this.lastAt = 0;
        this.width = 0;
        this.height = 0;
    },
    mounted() {
        if (this.show) {
            this.start();
        }
    },
    beforeDestroy() {
        this.stop();
    },
    watch: {
        show(value) {
            if (value) {
                this.start();
                return;
            }

            this.stop();
        },
    },
    methods: {
        start() {
            if (this.running) {
                return;
            }

            if (this.reducedMotion()) {
                this.$emit("finished");
                return;
            }

            this.running = true;

            this.$nextTick(() => {
                const canvas = this.$refs.canvas;

                if (!canvas) {
                    return;
                }

                this.ctx = canvas.getContext("2d");
                this.resize();
                this.buildParticles();

                window.addEventListener("resize", this.resize);

                this.startedAt = 0;
                this.frame = window.requestAnimationFrame(this.tick);
            });
        },

        tick(now) {
            if (!this.startedAt) {
                this.startedAt = now;
                this.lastAt = now;
            }

            const step = Math.min((now - this.lastAt) / 1000, MAX_STEP);
            this.lastAt = now;

            const emitting = now - this.startedAt < this.duration;
            const alive = this.update(step, emitting);

            this.render();

            if (!alive && !emitting) {
                this.finish();
                return;
            }

            this.frame = window.requestAnimationFrame(this.tick);
        },

        stop() {
            if (this.frame) {
                window.cancelAnimationFrame(this.frame);
                this.frame = null;
            }

            window.removeEventListener("resize", this.resize);
            this.running = false;
            this.ctx = null;
        },

        finish() {
            this.stop();
            this.$emit("finished");
        },

        buildParticles() {
            const total = Math.max(0, this.particleCount);

            this.particles = new Array(total);

            for (let i = 0; i < total; i++) {
                this.particles[i] = this.resetParticle({}, true);
            }
        },

        resetParticle(p, initial) {
            const rand = Math.random;
            const depth = 0.65 + rand() * 0.35;
            const draw = rand();

            p.shape = draw < 0.42 ? "rect" : draw < 0.72 ? "square" : "ribbon";

            if (p.shape === "ribbon") {
                p.w = (3 + rand() * 2) * depth;
                p.h = (11 + rand() * 6) * depth;
            } else if (p.shape === "square") {
                p.w = (6 + rand() * 5) * depth;
                p.h = p.w;
            } else {
                p.w = (6 + rand() * 5) * depth;
                p.h = p.w * (1.4 + rand() * 0.8);
            }

            p.color = this.colors[(rand() * this.colors.length) | 0];
            p.alpha = 0.78 + rand() * 0.22;
            p.bend = (rand() * 2 - 1) * 0.55;

            p.x = rand() * this.width;
            p.y = initial ? -rand() * this.height * 0.9 - 20 : -20 - rand() * 80;

            p.vx = (rand() * 2 - 1) * 18;
            p.vy = (40 + rand() * 60) * depth;
            p.gravity = (300 + rand() * 180) * depth;
            p.maxFall = (300 + rand() * 160) * depth;

            p.drift = 14 + rand() * 24;
            p.wobble = rand() * Math.PI * 2;
            p.wobbleSpeed = 0.8 + rand() * 1.4;

            p.rotation = rand() * Math.PI * 2;
            p.spin = (rand() * 2 - 1) * 3.2;
            p.flip = rand() * Math.PI * 2;
            p.flipSpeed = 1.4 + rand() * 2.6;

            p.done = false;

            return p;
        },

        update(step, emitting) {
            const list = this.particles;
            const floor = this.height + 40;
            const margin = 24;
            let alive = false;

            for (let i = 0; i < list.length; i++) {
                const p = list[i];

                if (p.done) {
                    continue;
                }

                p.vy = Math.min(p.vy + p.gravity * step, p.maxFall);
                p.wobble += p.wobbleSpeed * step;

                p.x += (p.vx + Math.sin(p.wobble) * p.drift) * step;
                p.y += p.vy * step;
                p.rotation += p.spin * step;
                p.flip += p.flipSpeed * step;

                if (p.x < -margin) {
                    p.x = this.width + margin;
                } else if (p.x > this.width + margin) {
                    p.x = -margin;
                }

                if (p.y > floor) {
                    if (!emitting) {
                        p.done = true;
                        continue;
                    }

                    this.resetParticle(p, false);
                }

                alive = true;
            }

            return alive;
        },

        render() {
            const ctx = this.ctx;

            if (!ctx) {
                return;
            }

            ctx.clearRect(0, 0, this.width, this.height);

            const list = this.particles;

            for (let i = 0; i < list.length; i++) {
                if (!list[i].done) {
                    this.drawParticle(ctx, list[i]);
                }
            }
        },

        drawParticle(ctx, p) {
            const hw = p.w / 2;
            const hh = p.h / 2;

            ctx.save();
            ctx.translate(p.x, p.y);
            ctx.rotate(p.rotation);
            ctx.scale(1, Math.cos(p.flip));
            ctx.globalAlpha = p.alpha;
            ctx.fillStyle = p.color;

            if (p.shape === "ribbon") {
                const bend = p.bend * p.w * 1.6;

                ctx.beginPath();
                ctx.moveTo(-hw, -hh);
                ctx.lineTo(hw, -hh);
                ctx.quadraticCurveTo(hw + bend, 0, hw, hh);
                ctx.lineTo(-hw, hh);
                ctx.quadraticCurveTo(-hw + bend, 0, -hw, -hh);
                ctx.closePath();
                ctx.fill();
            } else {
                ctx.fillRect(-hw, -hh, p.w, p.h);
            }

            ctx.restore();
        },

        resize() {
            const canvas = this.$refs.canvas;

            if (!canvas) {
                return;
            }

            const width = canvas.offsetWidth || window.innerWidth;
            const height = canvas.offsetHeight || window.innerHeight;

            if (this.width && this.particles.length) {
                const ratio = width / this.width;

                for (let i = 0; i < this.particles.length; i++) {
                    this.particles[i].x *= ratio;
                }
            }

            this.width = width;
            this.height = height;

            const density = Math.min(window.devicePixelRatio || 1, 2);

            canvas.width = Math.round(width * density);
            canvas.height = Math.round(height * density);

            if (this.ctx) {
                this.ctx.setTransform(density, 0, 0, density, 0, 0);
            }
        },

        reducedMotion() {
            return !!(
                window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
            );
        },
    },
};
</script>

<style lang="scss">
.confetti-rain {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
}
</style>
